import {
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  UpdateLinkedWorkspacesPayload,
  updateLinkedWorkspaces,
} from 'models/actions';
import {
  APIReferenceAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { WorkspaceChanges, workspacesClosed$ } from './context/context$';

const updateLinkedWorkspacesFromReferences = (
  currentLinkedWorkspaces: LinkedWorkspaces,
  references: APIReferenceAttributes[]
) => {
  const linkedWorkspaces = new Map(
    Array.from(currentLinkedWorkspaces).map(([wsId, linkedWsIds]) => [
      wsId,
      new Set(linkedWsIds),
    ])
  );
  references.forEach(
    ({
      rootWorkspace: rootWorkspaceId,
      targetWorkspace: targetWorkspaceId,
    }: APIReferenceAttributes) => {
      if (
        rootWorkspaceId &&
        targetWorkspaceId &&
        rootWorkspaceId !== targetWorkspaceId
      ) {
        populateMap(linkedWorkspaces, rootWorkspaceId, targetWorkspaceId);
      }
    }
  );

  return linkedWorkspaces;
};

// Temporary till we have the new presentation aggregated endpoint.
const resetWithLinkedWorkspaces = (
  workspaces: APIWorkspaceAttributes[],
  references: APIReferenceAttributes[]
) => {
  const linkedWorkspaces = new Map();
  workspaces.forEach(({ _id }) => {
    const linkedWorkspaceIds = references
      .filter(({ rootWorkspace, targetWorkspace }) => {
        if (rootWorkspace === targetWorkspace) return false;
        return rootWorkspace === _id || targetWorkspace === _id;
      })
      .map(({ rootWorkspace, targetWorkspace }) => {
        return rootWorkspace === _id ? targetWorkspace : rootWorkspace;
      });

    new Set(linkedWorkspaceIds).forEach(linkedId =>
      populateMap(linkedWorkspaces, _id, linkedId)
    );
  });

  return linkedWorkspaces;
};

const populateMap = (
  linkedWorkspaces: LinkedWorkspaces,
  sourceWorkspaceId: ArdoqId,
  targetWorkspaceId: ArdoqId
) => {
  let linkedTargetWorkspaces = linkedWorkspaces.get(sourceWorkspaceId);
  if (!linkedTargetWorkspaces) {
    linkedTargetWorkspaces = new Set();
    linkedWorkspaces.set(sourceWorkspaceId, linkedTargetWorkspaces);
  }
  linkedTargetWorkspaces.add(targetWorkspaceId);

  let linkedSourceWorkspaces = linkedWorkspaces.get(targetWorkspaceId);
  if (!linkedSourceWorkspaces) {
    linkedSourceWorkspaces = new Set();
    linkedWorkspaces.set(targetWorkspaceId, linkedSourceWorkspaces);
  }
  linkedSourceWorkspaces.add(sourceWorkspaceId);
};

export type LinkedWorkspaces = Map<ArdoqId, Set<ArdoqId>>;

const handleUpdateLinkedWorkspaces = (
  linkedWorkspaces: LinkedWorkspaces,
  { references, workspaces }: UpdateLinkedWorkspacesPayload
) =>
  workspaces
    ? resetWithLinkedWorkspaces(workspaces, references)
    : updateLinkedWorkspacesFromReferences(linkedWorkspaces, references);

const handleWorkspacesClosed = (
  linkedWorkspaces: LinkedWorkspaces,
  { workspaceIds }: WorkspaceChanges
) => {
  if (workspaceIds.length === 0) {
    return new Map();
  }

  // When we close workspaces we keep only the references which have either
  // the source or target (or both) workspace in the set of open workspaces.
  // Accordingly we can keep any open workspace and for any closed workspace
  // all linked workspaces which are still open.
  const openIds = new Set(workspaceIds);
  return new Map(
    Array.from(linkedWorkspaces)
      .map<[string, Set<string>]>(([wsId, linkedIds]) => {
        if (openIds.has(wsId)) {
          return [wsId, linkedIds];
        }

        const stillLinkedWorkspaces = Array.from(linkedIds).filter(linkedId =>
          openIds.has(linkedId)
        );
        return [wsId, new Set(stillLinkedWorkspaces)];
      })
      .filter(([, linkedIds]) => linkedIds.size > 0)
  );
};

export const defaultState = (): LinkedWorkspaces =>
  new Map<ArdoqId, Set<ArdoqId>>();

const reducers = [
  reducer(updateLinkedWorkspaces, handleUpdateLinkedWorkspaces),
  streamReducer(workspacesClosed$, handleWorkspacesClosed),
];

export const linkedWorkspaces$ = persistentReducedStream<LinkedWorkspaces>(
  'linkedWorkspaces$',
  defaultState(),
  reducers
);

// Returns the connected workspaces, including the input set, one step in and
// out matching the pattern how we load workspaces.
export const getConnectedWorkspaceIds = (
  workspaceIds: ArdoqId[],
  linkedWorkspaces: LinkedWorkspaces
) =>
  new Set(
    workspaceIds.flatMap(id => [
      id,
      ...Array.from(linkedWorkspaces.get(id) ?? []),
    ])
  );

// Returns workspace IDs linked to the input workspaces, excluding the input workspaces themselves.
export const getLinkedWorkspaceIds = (
  workspaceIds: ArdoqId[],
  linkedWorkspaces: LinkedWorkspaces
): ArdoqId[] => {
  const input = new Set(workspaceIds);
  const linked = new Set(
    workspaceIds.flatMap(id => Array.from(linkedWorkspaces.get(id) ?? []))
  );

  return Array.from(linked.difference(input));
};

// Gets the connected workspaces with a DFS, excluding the input workspace.
export const getConnectedWorkspaceIdsDeep = (
  workspaceId: ArdoqId,
  linkedWorkspaces: LinkedWorkspaces
): ArdoqId[] => {
  const visited = workspaceDepthFirstSearch(workspaceId, linkedWorkspaces);
  visited.delete(workspaceId);
  return Array.from(visited);
};

const workspaceDepthFirstSearch = (
  workspaceId: ArdoqId,
  linkedWorkspaces: LinkedWorkspaces,
  visited = new Set<ArdoqId>()
): Set<ArdoqId> => {
  if (!visited.has(workspaceId)) {
    visited.add(workspaceId);
    Array.from(linkedWorkspaces.get(workspaceId) ?? []).forEach(id =>
      workspaceDepthFirstSearch(id, linkedWorkspaces, visited)
    );
  }
  return visited;
};
